import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader, Spinner } from '@/modules/common';
import { Routes } from '@/router/routes';

import { useStyleDetails } from '../api';
import { StyleForm } from '../components';
import { StyleItem } from '../types';

export const StyleDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const {
    details,
    uploadUrlData,
    isFetching,
    refetchUploadUrl,
    updateStyleMutation,
  } = useStyleDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      loraModelTriggerWord: details.loraModelTriggerWord || '',
      loraModelStrength: details.loraModelStrength || undefined,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
      loraEnabled: false,
    };
  }, [details]);

  const updateModel = async (data) => {
    if (uploadUrlData?.styleId) {
      try {
        const body = deepDifference<StyleItem>(data, defaultValues);
        await updateStyleMutation.mutateAsync({
          ...body,
          id: params.id,
        });
        toast.success(`Style “${data.name}” updated successfully!`);
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <StyleForm
      editMode
      onSubmit={updateModel}
      defaultValues={defaultValues}
      uploadUrlData={uploadUrlData}
      refetchUploadCredentials={refetchUploadCredentials}
    >
      {({ isLoading }) => (
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Styles}
          actions={[
            <Button
              key="save-style"
              type="submit"
              data-testid="save-style-button"
              isLoading={
                isFetching || isLoading || updateStyleMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      )}
    </StyleForm>
  );
};
